import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus, Radio, Trash2 } from "lucide-react";

type Category = "any" | "accuracy" | "injection" | "citation" | "rls" | "other";
type Severity = "warn" | "fail";

type NotifyChannel = "inapp" | "slack" | "email";
type Route = {
  id: string;
  category: Category;
  label: string;
  webhook_url: string;
  min_severity: Severity;
  enabled: boolean;
  created_at: string;
  failure_threshold: number | null;
  failure_window_minutes: number;
  threshold_notify_channel: NotifyChannel;
  threshold_notify_target: string | null;
  threshold_quiet_start_hour: number | null;
  threshold_quiet_end_hour: number | null;
  threshold_quiet_tz: string;
};

const CATEGORY_HELP: Record<Category, string> = {
  any: "Fires on any eval regression (catch-all).",
  accuracy: "Nightly answer accuracy fell below baseline.",
  injection: "Red-team injection prompts were not refused.",
  citation: "Answers reference chunks that were not retrieved.",
  rls: "Spike in denied / RLS-blocked tool calls during eval.",
  other: "Uncategorized failure (e.g. edge function crash).",
};

// Mask webhook URLs in the UI — full URL is a secret and only the last path
// segment is shown for identification. Editing requires re-entering the URL.
function maskWebhook(url: string) {
  try {
    const u = new URL(url);
    const tail = u.pathname.split("/").filter(Boolean).pop() ?? "";
    return `${u.host}/…/${tail.slice(0, 6)}…`;
  } catch {
    return "invalid url";
  }
}

export function HelpdeskAlertRoutesPanel() {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<{ category: Category; label: string; webhook_url: string; min_severity: Severity }>({
    category: "accuracy", label: "", webhook_url: "", min_severity: "fail",
  });

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_routes")
      .select("id, category, label, webhook_url, min_severity, enabled, created_at, failure_threshold, failure_window_minutes, threshold_notify_channel, threshold_notify_target, threshold_quiet_start_hour, threshold_quiet_end_hour, threshold_quiet_tz")
      .order("created_at", { ascending: true });
    if (error) toast.error(`Load routes: ${error.message}`);
    else setRoutes((data ?? []) as unknown as Route[]);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const add = async () => {
    if (!draft.label.trim() || !draft.webhook_url.trim()) {
      toast.error("Label and webhook URL required");
      return;
    }
    try {
      new URL(draft.webhook_url);
    } catch {
      toast.error("Webhook URL must be a full https:// URL");
      return;
    }
    setSaving(true);
    const { error } = await supabase.from("helpdesk_alert_routes").insert({
      category: draft.category,
      label: draft.label.trim().slice(0, 80),
      webhook_url: draft.webhook_url.trim(),
      min_severity: draft.min_severity,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    setDraft({ category: "accuracy", label: "", webhook_url: "", min_severity: "fail" });
    toast.success("Route added");
    void load();
  };

  const toggle = async (r: Route) => {
    const { error } = await supabase.from("helpdesk_alert_routes")
      .update({ enabled: !r.enabled, updated_at: new Date().toISOString() })
      .eq("id", r.id);
    if (error) toast.error(error.message);
    else void load();
  };

  const remove = async (r: Route) => {
    if (!confirm(`Delete route "${r.label}"?`)) return;
    const { error } = await supabase.from("helpdesk_alert_routes").delete().eq("id", r.id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); void load(); }
  };

  const updateThreshold = async (r: Route, threshold: number | null, windowMinutes: number) => {
    const { error } = await supabase.from("helpdesk_alert_routes")
      .update({
        failure_threshold: threshold,
        failure_window_minutes: Math.max(5, Math.min(1440, windowMinutes || 60)),
        updated_at: new Date().toISOString(),
      })
      .eq("id", r.id);
    if (error) toast.error(error.message);
    else { toast.success("Threshold saved"); void load(); }
  };

  const updateNotify = async (
    r: Route,
    channel: NotifyChannel,
    target: string,
    quietStart: string,
    quietEnd: string,
    tz: string,
  ): Promise<void> => {
    if (channel === "slack" && target && !/^https:\/\/hooks\.slack\.com\//.test(target)) {
      toast.error("Slack target must be a hooks.slack.com URL");
      return;
    }
    if (channel === "email" && target && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Email target must be a valid address");
      return;
    }
    const parseHour = (v: string) => {
      if (v === "" || v == null) return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > 23) return null;
      return Math.floor(n);
    };
    const { error } = await supabase.from("helpdesk_alert_routes")
      .update({
        threshold_notify_channel: channel,
        threshold_notify_target: target.trim() || null,
        threshold_quiet_start_hour: parseHour(quietStart),
        threshold_quiet_end_hour: parseHour(quietEnd),
        threshold_quiet_tz: tz.trim() || "UTC",
        updated_at: new Date().toISOString(),
      })
      .eq("id", r.id);
    if (error) toast.error(error.message);
    else { toast.success("Notification saved"); void load(); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radio className="size-4 text-primary" /> Alert routing
        </CardTitle>
        <CardDescription>
          Route nightly eval failures to different Slack channels by failure category. A single failing run
          can match multiple categories and fan out to every enabled route.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 md:grid-cols-[160px_1fr_1fr_140px_auto] items-end">
          <div>
            <label className="text-xs text-muted-foreground">Category</label>
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v as Category })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(CATEGORY_HELP) as Category[]).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Label</label>
            <Input placeholder="#eng-helpdesk" value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Slack webhook URL</label>
            <Input type="password" placeholder="https://hooks.slack.com/services/…"
              value={draft.webhook_url}
              onChange={(e) => setDraft({ ...draft, webhook_url: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Min severity</label>
            <Select value={draft.min_severity} onValueChange={(v) => setDraft({ ...draft, min_severity: v as Severity })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warn">warn (drift ≥ 1pp)</SelectItem>
                <SelectItem value="fail">fail (below baseline)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            <span className="ml-1">Add</span>
          </Button>
        </div>
        <p className="-mt-4 text-[11px] text-muted-foreground">
          {CATEGORY_HELP[draft.category]}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">Category</th>
                <th className="py-2 pr-4 font-medium">Label</th>
                <th className="py-2 pr-4 font-medium">Webhook</th>
                <th className="py-2 pr-4 font-medium">Severity</th>
                <th className="py-2 pr-4 font-medium" title="Notify when this many failures happen within the window">
                  Failure threshold
                </th>
                <th className="py-2 pr-4 font-medium" title="Where threshold breaches are sent and when to stay quiet">
                  Threshold notify
                </th>
                <th className="py-2 pr-4 font-medium">Enabled</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-4 text-center"><Loader2 className="size-4 animate-spin inline" /></td></tr>
              ) : routes.length === 0 ? (
                <tr><td colSpan={8} className="py-4 text-center text-xs text-muted-foreground">No routes — eval failures won't notify anyone until you add one.</td></tr>
              ) : routes.map((r) => (
                <tr key={r.id} className="border-b border-border/50 last:border-0 align-top">
                  <td className="py-2 pr-4"><Badge variant="secondary">{r.category}</Badge></td>
                  <td className="py-2 pr-4">{r.label}</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{maskWebhook(r.webhook_url)}</td>
                  <td className="py-2 pr-4 text-xs">{r.min_severity}</td>
                  <td className="py-2 pr-4">
                    <ThresholdCell route={r} onSave={updateThreshold} />
                  </td>
                  <td className="py-2 pr-4">
                    <NotifyCell route={r} onSave={updateNotify} />
                  </td>
                  <td className="py-2 pr-4"><Switch checked={r.enabled} onCheckedChange={() => void toggle(r)} /></td>
                  <td className="py-2 text-right">
                    <Button variant="ghost" size="icon-sm" onClick={() => void remove(r)} aria-label="Delete route">
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ThresholdCell({
  route,
  onSave,
}: {
  route: Route;
  onSave: (r: Route, threshold: number | null, windowMinutes: number) => Promise<void>;
}) {
  const [threshold, setThreshold] = useState<string>(
    route.failure_threshold != null ? String(route.failure_threshold) : "",
  );
  const [window, setWindow] = useState<string>(String(route.failure_window_minutes ?? 60));
  const dirty = (route.failure_threshold ?? "") + "|" + (route.failure_window_minutes ?? 60) !==
    threshold + "|" + window;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <Input
        className="h-7 w-16 px-2"
        type="number"
        min={1}
        placeholder="off"
        value={threshold}
        onChange={(e) => setThreshold(e.target.value)}
        aria-label="Failure threshold"
      />
      <span className="text-muted-foreground">in</span>
      <Input
        className="h-7 w-16 px-2"
        type="number"
        min={5}
        max={1440}
        value={window}
        onChange={(e) => setWindow(e.target.value)}
        aria-label="Failure window minutes"
      />
      <span className="text-muted-foreground">min</span>
      {dirty && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() =>
            void onSave(
              route,
              threshold.trim() === "" ? null : Math.max(1, Number(threshold) || 0),
              Number(window) || 60,
            )
          }
        >
          Save
        </Button>
      )}
    </div>
  );
}

function NotifyCell({
  route,
  onSave,
}: {
  route: Route;
  onSave: (
    r: Route,
    channel: NotifyChannel,
    target: string,
    quietStart: string,
    quietEnd: string,
    tz: string,
  ) => Promise<void>;
}) {
  const [channel, setChannel] = useState<NotifyChannel>(route.threshold_notify_channel ?? "inapp");
  const [target, setTarget] = useState<string>(route.threshold_notify_target ?? "");
  const [qs, setQs] = useState<string>(route.threshold_quiet_start_hour != null ? String(route.threshold_quiet_start_hour) : "");
  const [qe, setQe] = useState<string>(route.threshold_quiet_end_hour != null ? String(route.threshold_quiet_end_hour) : "");
  const [tz, setTz] = useState<string>(route.threshold_quiet_tz ?? "UTC");

  const dirty =
    channel !== (route.threshold_notify_channel ?? "inapp") ||
    (target || "") !== (route.threshold_notify_target ?? "") ||
    qs !== (route.threshold_quiet_start_hour != null ? String(route.threshold_quiet_start_hour) : "") ||
    qe !== (route.threshold_quiet_end_hour != null ? String(route.threshold_quiet_end_hour) : "") ||
    tz !== (route.threshold_quiet_tz ?? "UTC");

  return (
    <div className="flex flex-col gap-1.5 text-xs min-w-[240px]">
      <div className="flex items-center gap-1.5">
        <Select value={channel} onValueChange={(v) => setChannel(v as NotifyChannel)}>
          <SelectTrigger className="h-7 w-[92px] px-2 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inapp">In-app</SelectItem>
            <SelectItem value="slack">Slack</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        {channel !== "inapp" && (
          <Input
            className="h-7 px-2 text-xs"
            type={channel === "email" ? "email" : "url"}
            placeholder={channel === "email" ? "alerts@team.com" : "https://hooks.slack.com/…"}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label="Notify target"
          />
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground">Quiet</span>
        <Input className="h-7 w-12 px-2" type="number" min={0} max={23} placeholder="—"
          value={qs} onChange={(e) => setQs(e.target.value)} aria-label="Quiet start hour" />
        <span className="text-muted-foreground">–</span>
        <Input className="h-7 w-12 px-2" type="number" min={0} max={23} placeholder="—"
          value={qe} onChange={(e) => setQe(e.target.value)} aria-label="Quiet end hour" />
        <Input className="h-7 w-24 px-2" placeholder="UTC"
          value={tz} onChange={(e) => setTz(e.target.value)} aria-label="Timezone" />
        {dirty && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
            onClick={() => void onSave(route, channel, target, qs, qe, tz)}>
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

export default HelpdeskAlertRoutesPanel;
